import { getUiLocale } from '../../utils/uiTranslation';
import { uiText, useUiLanguage } from '../../utils/uiTranslation';
// Certificate Verification Page
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ref, get } from 'firebase/database';
import { database } from '../../firebase';

async function readOnce(path) {
  const snapshot = await get(ref(database, path));
  return snapshot.exists() ? snapshot.val() : null;
}

export default function VerifyCertPage() {
  useUiLanguage();
  const { slug } = useParams();

  const [loading, setLoading] = useState(true);
  const [verification, setVerification] = useState(null);
  const [recipientName, setRecipientName] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    async function run() {
      if (!slug) return;
      setLoading(true);
      setError(null);

      try {
        // Verification records contain public credential evidence, never the artifact.
        const v = await readOnce(`verifications/${slug}`);
        if (!v) {
          setError(uiText('Credential not found.'));
          setLoading(false);
          return;
        }
        setVerification(v);

        // Resolve recipient name from the immutable snapshot when available.
        if (v.recipientName && String(v.recipientName).trim()) {
          setRecipientName(v.recipientName);
        } else if (v.userId) {
          // Fallback: single additional read if snapshot wasn't saved (legacy)
          const user = await readOnce(`people/${v.userId}`);
          setRecipientName((user && user.username) || 'JOBSEEQR User');
        } else {
          setRecipientName('JOBSEEQR User');
        }

        setLoading(false);
      } catch (e) {
        setError(e?.message || 'Unexpected error.');
        setLoading(false);
      }
    }
    run();
  }, [slug]);

  const container = { padding: 24, fontFamily: 'system-ui' };
  const muted = { color: '#666' };
  const strong = { fontWeight: 600 };
  const okColor = { color: '#1b5e20' };
  const errColor = { color: '#b71c1c' };

  if (loading) {
    return (
      <main style={container}>
        <h2>{uiText("Verifying certificate…")}</h2>
      </main>
    );
  }

  if (error || !verification) {
    return (
      <main style={container}>
        <h2>{uiText("Verification")}</h2>
        <p style={errColor}>{error || uiText("Credential not found.")}</p>
      </main>
    );
  }

  const issuedAt = verification.issuedAt
    ? new Date(verification.issuedAt).toLocaleString(getUiLocale())
    : 'N/A';
  const link = verification.verifyUrl || (typeof window !== 'undefined' ? window.location.href : '');
  const assessment = verification.assessmentSummary || null;
  const isAssessedBadge = verification.verificationType === 'assessed_skill_badge';
  const assessedSteps = Array.isArray(assessment?.assessedSteps) ? assessment.assessedSteps : [];
  const criteria = Array.isArray(assessment?.criteria) ? assessment.criteria : [];
  const panel = {
    marginTop: 18,
    padding: 18,
    border: '1px solid #d7e0ec',
    borderRadius: 8,
    background: '#f8fafc',
  };
  const evidenceList = { margin: '10px 0 0', paddingLeft: 20, lineHeight: 1.55 };

  return (
    <main style={container}>
      <h1>{uiText("JOBSEEQR Credential Verification")}</h1>
      <section style={{ marginTop: 16 }}>
        <p><span style={strong}>{uiText("Status:")}</span> <span style={okColor}>{uiText("Verified")}</span></p>
        <p><span style={strong}>{uiText("Recipient:")}</span> {recipientName}</p>
        <p><span style={strong}>{uiText("Credential:")}</span> {verification.certificateTitle || verification.badgeTitle}</p>
        <p><span style={strong}>{uiText("Type:")}</span> {isAssessedBadge ? uiText("Assessed skill badge") : verification.verificationType === 'assessed_pathway_certificate' ? uiText("Assessed pathway certificate") : uiText("Certificate of completion")}</p>
        <p><span style={strong}>{uiText("Issued:")}</span> {issuedAt}</p>
        <p><span style={strong}>{uiText("Verification Link:")}</span> {link}</p>
      </section>

      {assessment && (
        <section style={panel} aria-labelledby="assessment-evidence-title">
          <h2 id="assessment-evidence-title" style={{ marginTop: 0 }}>{uiText("Assessment evidence")}</h2>
          <p><span style={strong}>{uiText("Method:")}</span> {assessment.assessmentMethod}</p>
          {assessment.simulation && (
            <p style={muted}>{uiText("This credential records demonstrated work in a contextualized simulation. It does not claim verified identity, unaided work, real-world business impact, or unsupported technical execution.")}</p>
          )}
          {assessment.credentialClaim && <p><span style={strong}>{uiText("Demonstrated claim:")}</span> {assessment.credentialClaim}</p>}
          {criteria.length > 0 && (
            <div>
              <span style={strong}>{uiText("Demonstrated criteria")}</span>
              <ul style={evidenceList}>{criteria.map((criterion) => <li key={criterion.label}>{uiText(criterion.label)}</li>)}</ul>
            </div>
          )}
          {assessment.defenseCompleted && <p><span style={strong}>{uiText("Text defense:")}</span>{uiText(" Completed")}</p>}
          {assessment.aiUseDisclosureCompleted && <p><span style={strong}>{uiText("AI-use disclosure:")}</span>{uiText(" Completed")}</p>}
          {assessment.artifactHash && <p><span style={strong}>{uiText("Artifact fingerprint:")}</span> <code>{assessment.artifactHash}</code></p>}
          {assessedSteps.length > 0 && (
            <div>
              <span style={strong}>{uiText("Assessed pathway steps")}</span>
              {assessedSteps.map((step) => (
                <article key={`${step.stepNumber}-${step.stepTitle}`} style={{ marginTop: 12 }}>
                  <strong>{step.stepNumber}. {step.stepTitle}</strong>
                  {step.credentialClaim && <p style={{ marginBottom: 6 }}>{step.credentialClaim}</p>}
                  <ul style={evidenceList}>{(step.criteria || []).map((criterion) => <li key={criterion}>{criterion}</li>)}</ul>
                </article>
              ))}
            </div>
          )}
        </section>
      )}
    </main>
  );
}
